import { createSlice, PayloadAction } from '@reduxjs/toolkit';

import { TRiskLevel } from '@interfaces/riskLevels';
import { InquiryResponse } from '@models/inquiryResponse';
import eventBus, { EventType } from '@utils/eventBus';

import {
  handleMarkMedicationResponseAsViewed,
  handleSubmitMedicationNeedsInfoForm,
} from '../medications/userEnteredMedication/actions';

import {
  handleGetInquiryResponse,
  handleGetMedicationResponseDetails,
  handleGetPrintInquiryResponse,
  handleGetSupplementResponseDetails,
  handleGetUserEnteredSupplement,
  handleMarkSupplementResponseAsViewed,
  handleSubmitSupplementNeedsInfoForm,
} from './actions';
import { IInquiryResponseState } from './slice.interface';
import {
  updateActiveMedicationStatusToComplete,
  updateActiveSupplementStatusToComplete,
  updateInquiryStatusAndListItemStatusToComplete,
  updateInquiryStatusAndMedicationsStatusToComplete,
} from './utils';

const initialState: IInquiryResponseState = {
  data: null,
  printData: null,
  isLoading: false,
  error: false,
  activeSupplementId: null,
  activeSupplementData: null,
  activeSupplementIsLoading: false,
  activeSupplementError: false,
  userEnteredSupplement: null,
  userEnteredSupplementIsLoading: false,
  userEnteredSupplementError: false,
  showRiskLevelDetails: null,
  activeMedicationId: null,
  activeMedicationData: null,
  activeMedicationIsLoading: false,
  activeMedicationError: false,
};

export const inquiryResponseSlice = createSlice({
  name: 'inquiry-response',
  initialState,
  reducers: {
    removeInquiryResponseDetails: (state: IInquiryResponseState) => {
      state.data = null;
      state.printData = null;
    },
    removeActiveSupplement: (state: IInquiryResponseState) => {
      state.activeSupplementId = null;
      state.activeSupplementData = null;
      state.activeSupplementIsLoading = false;
      state.activeSupplementError = false;
      state.userEnteredSupplement = null;
      state.userEnteredSupplementIsLoading = false;
      state.userEnteredSupplementError = false;
    },
    removeActiveMedication: (state: IInquiryResponseState) => {
      state.activeMedicationId = null;
      state.activeMedicationData = null;
      state.activeMedicationIsLoading = false;
      state.activeMedicationError = false;
    },
    setShowRiskLevelDetails: (
      state: IInquiryResponseState,
      action: PayloadAction<TRiskLevel | null>,
    ) => {
      state.showRiskLevelDetails = action.payload;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(handleGetInquiryResponse.pending, (state: IInquiryResponseState) => {
        state.isLoading = true;
        state.error = false;
      })
      .addCase(handleGetInquiryResponse.rejected, (state: IInquiryResponseState) => {
        state.isLoading = false;
        state.error = true;
      })
      .addCase(handleGetInquiryResponse.fulfilled, (state: IInquiryResponseState, action) => {
        state.data = new InquiryResponse(action?.payload?.data);
        state.isLoading = false;
      })
      .addCase(handleGetPrintInquiryResponse.pending, (state: IInquiryResponseState) => {
        state.isLoading = true;
        state.error = false;
      })
      .addCase(handleGetPrintInquiryResponse.rejected, (state: IInquiryResponseState) => {
        state.isLoading = false;
        state.error = true;
      })
      .addCase(handleGetPrintInquiryResponse.fulfilled, (state: IInquiryResponseState, action) => {
        state.printData = action.payload.data;
        state.isLoading = false;
      })
      .addCase(
        handleGetSupplementResponseDetails.pending,
        (state: IInquiryResponseState, action) => {
          state.activeSupplementId = action.meta.arg;
          state.activeSupplementData = null;
          state.activeSupplementIsLoading = true;
          state.activeSupplementError = false;
        },
      )
      .addCase(handleGetSupplementResponseDetails.rejected, (state: IInquiryResponseState) => {
        state.activeSupplementIsLoading = false;
        state.activeSupplementError = true;
      })
      .addCase(
        handleGetSupplementResponseDetails.fulfilled,
        (state: IInquiryResponseState, action) => {
          state.activeSupplementData = action.payload.data;
          state.activeSupplementIsLoading = false;
        },
      )
      .addCase(handleMarkSupplementResponseAsViewed.fulfilled, (state: IInquiryResponseState) => {
        if (state.data && state.activeSupplementData) {
          state.data = updateInquiryStatusAndListItemStatusToComplete(
            state.data,
            state.activeSupplementData.id,
          );
          state.activeSupplementData = updateActiveSupplementStatusToComplete(
            state.activeSupplementData,
          );
        }
      })
      .addCase(handleMarkMedicationResponseAsViewed.fulfilled, (state: IInquiryResponseState) => {
        if (state.data && state.activeMedicationData) {
          state.data = updateInquiryStatusAndMedicationsStatusToComplete(
            state.data,
            state.activeMedicationData.userEnteredMedicationId,
          );
          state.activeMedicationData = updateActiveMedicationStatusToComplete(
            state.activeMedicationData,
          );
        }
      })
      .addCase(handleGetUserEnteredSupplement.pending, (state: IInquiryResponseState) => {
        state.userEnteredSupplement = null;
        state.userEnteredSupplementIsLoading = true;
        state.userEnteredSupplementError = false;
      })
      .addCase(handleGetUserEnteredSupplement.rejected, (state: IInquiryResponseState) => {
        state.userEnteredSupplementIsLoading = false;
        state.userEnteredSupplementError = true;
      })
      .addCase(handleGetUserEnteredSupplement.fulfilled, (state: IInquiryResponseState, action) => {
        state.userEnteredSupplement = action.payload.data;
        state.userEnteredSupplementIsLoading = false;
      })
      .addCase(handleSubmitSupplementNeedsInfoForm.fulfilled, () => {
        eventBus.emit(EventType.NEEDS_INFO_FORM_SUBMITTED);
      })
      .addCase(handleSubmitMedicationNeedsInfoForm.fulfilled, () => {
        eventBus.emit(EventType.MEDICATION_NEEDS_INFO_FORM_SUBMITTED);
      })
      .addCase(
        handleGetMedicationResponseDetails.pending,
        (state: IInquiryResponseState, action) => {
          state.activeMedicationId = action.meta.arg;
          state.activeMedicationData = null;
          state.activeMedicationIsLoading = true;
          state.activeMedicationError = false;
        },
      )
      .addCase(handleGetMedicationResponseDetails.rejected, (state: IInquiryResponseState) => {
        state.activeMedicationIsLoading = false;
        state.activeMedicationError = true;
      })
      .addCase(
        handleGetMedicationResponseDetails.fulfilled,
        (state: IInquiryResponseState, action) => {
          state.activeMedicationData = action.payload.data;
          state.activeMedicationIsLoading = false;
        },
      );
  },
});

export const {
  removeActiveSupplement,
  removeInquiryResponseDetails,
  setShowRiskLevelDetails,
  removeActiveMedication,
} = inquiryResponseSlice.actions;

export default inquiryResponseSlice.reducer;
